import type { FileSystemTree } from "@webcontainer/api";
import { merge, sample } from "lodash";
import * as Y from "yjs";

export enum FileType {
	Directory = "directory",
	File = "file",
}

export interface FileObject {
	// The fully qualified path to the file
	path: string;
	// If the file is a directory, or a file
	type: FileType;
	// The actual contents of the file
	contents?: Y.Text;
}

// Every file object consists of a type, and contents. The key is the path.
export type YFileSystemObject = Y.Map<Y.Text>;

export class FileSystem {
	private rootDoc: Y.Doc;
	public fsDoc: Y.Map<YFileSystemObject>;

	constructor(ydoc: Y.Doc) {
		this.rootDoc = ydoc;
		this.fsDoc = this.rootDoc.getMap("fs");

		// Alwas create a root directory, if it doesn't exist
		const rootDir = this.fsDoc.get("/");
		if (!rootDir) {
			this.mkdir("/");
		}
	}

	private normalizePath(path: string, isDirectory: boolean): string {
		if (!path) {
			return "/"; // Treat empty path as root, you can adjust this policy
		}

		if (path === "/") {
			return "/"; // Root path is already normalized
		}

		let normalizedPath = path;

		// Ensure leading slash for absolute paths
		if (!normalizedPath.startsWith("/")) {
			normalizedPath = `/${normalizedPath}`;
		}

		// Remove trailing slash if it exists (unless it's just "/")
		if (normalizedPath !== "/" && normalizedPath.endsWith("/")) {
			normalizedPath = normalizedPath.slice(0, -1); // Remove the last character
		}

		if (isDirectory) {
			normalizedPath += "/"; // Add trailing slash for directories
		}

		return normalizedPath;
	}

	private getDirectChildren(parentPath: string, paths: string[]): string[] {
		const directChildren: string[] = [];
		// Ensure parentPath ends with a slash for consistent comparison and handle root path correctly
		const normalizedParentPath =
			parentPath === "/"
				? "/"
				: parentPath.endsWith("/")
					? parentPath
					: `${parentPath}/`;

		for (const path of paths) {
			if (
				path.startsWith(normalizedParentPath) &&
				path !== normalizedParentPath
			) {
				const remainingPath = path.substring(normalizedParentPath.length);
				const segments = remainingPath.split("/");
				// A direct child is one where there is only one segment at the top level, or two if its a directory with trailing slash
				if (
					segments.length === 1 ||
					(segments.length === 2 && segments[1] === "")
				) {
					directChildren.push(path);
				}
			}
		}
		return directChildren;
	}

	/**
	 * Converts the Yjs file system to a FileSystemTree for the webcontainer API.
	 * You almost certainly don't want to pass an argument, the default is "/"
	 *
	 * @returns {FileSystemTree} The file system tree.
	 */
	public toFileSystemTree(docPath = "/"): FileSystemTree {
		const doc = this.fsDoc.get(docPath);
		// base case. If the doc is empty, return empty
		if (!doc || doc.size === 0) {
			throw new Error("Doc not found at path");
		}

		// base case. If doc is file, return file node
		if (doc.get("type")?.toString() === "file") {
			const fileName = docPath.split("/").pop() as string;
			return {
				[fileName]: {
					file: {
						contents: doc.get("contents")?.toString() || "",
					},
				},
			} as FileSystemTree;
		}

		if (doc.get("type")?.toString() === "directory") {
			// Collect all keys that are in the `path` directory (not including `path` itself)
			const keysInDirectory: string[] = [];
			for (const key of this.fsDoc.keys()) {
				const qualifiedPath = this.normalizePath(docPath, true);
				if (key.startsWith(qualifiedPath) && key !== docPath) {
					keysInDirectory.push(key);
				}
			}

			// Now filter for only the paths on the current level
			keysInDirectory.sort();

			const topLevelPaths = this.getDirectChildren(docPath, keysInDirectory);
			const subTrees = topLevelPaths.map((key) => {
				return this.toFileSystemTree(key);
			});

			// Don't include the root path
			if (docPath === "/") {
				return merge({}, ...subTrees);
			}

			console.log("creating dir", docPath.replaceAll("/", ""), docPath);
			return {
				[docPath.replaceAll("/", "") as string]: {
					directory: merge({}, ...subTrees),
				},
			} as FileSystemTree;
		}

		throw new Error("doc must be a file or directory");
	}

	/**
	 * Given a file object, set the contents in the filesystem.
	 *
	 * @param {FileObject} object - The file object to set in the filesystem.
	 */
	private setYFileObject(object: FileObject) {
		const obj = new Y.Map<Y.Text>();
		obj.set("type", new Y.Text(object.type));
		if (object.type === FileType.File && object.contents) {
			obj.set("contents", object.contents);
		}

		this.fsDoc.set(object.path, obj);
	}

	/**
	 * Converts a Yjs file to a FileObject.
	 *
	 * @param {string} path - The path of the file.
	 * @param {boolean} [withContents=true] - Whether to include the contents of the file.
	 * @returns {FileObject} The file object.
	 * @throws {Error} If the file is not found.
	 */
	private yFileToFileObject(path: string, withContents = true): FileObject {
		const file = this.fsDoc.get(path);
		if (!file) {
			throw new Error("File not found");
		}

		// biome-ignore lint/style/noNonNullAssertion: This is for internal use only
		const type = file.get("type")!.toString() as FileType;

		if (!withContents) {
			return { path, type };
		}

		const contents = file.get("contents");

		return contents ? { path, type, contents } : { path, type };
	}

	/**
	 * Retrieves a directory from the filesystem.
	 *
	 * @param {string} path - The path of the directory.
	 * @returns {YFileSystemObject} The directory object.
	 * @throws {Error} If the directory is not found or the path is not a directory.
	 */
	private getDir(path: string) {
		const normalizedPath = this.normalizePath(path, true);
		const dir = this.fsDoc.get(normalizedPath);
		if (!dir) {
			throw new Error("Directory not found");
		}

		const type = dir?.get("type");
		if (type?.toString() !== "directory") {
			throw new Error("Path is not a directory");
		}
		return dir;
	}

	/**
	 * Retrieves a file from the filesystem.
	 *
	 * @param {string} path - The path of the file.
	 * @returns {YFileSystemObject} The file object.
	 * @throws {Error} If the file is not found or the path is not a file.
	 */
	public getFile(path: string) {
		const normalizedPath = this.normalizePath(path, false);
		const file = this.fsDoc.get(normalizedPath);
		if (!file) {
			throw new Error(`File not found: ${normalizedPath}`);
		}

		const type = file?.get("type");
		if (type?.toString() !== "file") {
			throw new Error(`Path is not a file: ${normalizedPath}`);
		}
		return file;
	}

	/**
	 * Iterates over the parent paths of a given file path.
	 *
	 * @param {string} sourcePath The fil1e path to iterate over.
	 * @returns {string[]} An array of parent paths.  Returns an empty array if the path is empty or just a root directory.
	 */
	private getParentPaths(path: string): string[] {
		const normalizedPath = this.normalizePath(path, false); // Normalize the input path first
		const parentPaths: string[] = [];
		let currentPath = normalizedPath;

		while (true) {
			const lastSlashIndex = currentPath.lastIndexOf(
				"/",
				currentPath.length - 2,
			); // Find the last slash before the very end (to avoid removing the leading slash if path is just "/")

			if (lastSlashIndex <= 0) {
				// 0 or less means no more parent paths to extract (reached root or empty path)
				break;
			}

			currentPath = currentPath.substring(0, lastSlashIndex + 1); // Get the path up to the last slash (inclusive to keep the slash for directory path)
			const normalizedParentPath = this.normalizePath(currentPath, true); // Normalize the parent path as a directory
			if (normalizedParentPath && normalizedParentPath !== "/") {
				// Ensure the normalized parent path is not just root "/"
				parentPaths.push(normalizedParentPath);
			}
			if (currentPath === "/") {
				//stop w
				//hen we reach root
				break;
			}
		}

		return parentPaths;
	}

	/**
	 * Checks if all parent directories exist in the filesystem.
	 *
	 * @param {string} path - The path to check.
	 * @returns {boolean} Whether all parent directories exist.
	 */
	private parentPathsExist(path: string): boolean {
		// Check that all parent directories exist
		return this.getParentPaths(path).every((parentPath) =>
			this.fsDoc.get(parentPath),
		);
	}

	/**
	 * Creates a new directory at the given path.
	 *
	 * @param {string} path - The path of the new directory.
	 * @throws {Error} If the directory already exists or parent directories do not exist.
	 */
	public mkdir(path: string) {
		const cleanPath = this.normalizePath(path, true);

		// First, check if the directory already exists
		const dir = this.fsDoc.get(cleanPath);

		if (dir) {
			throw new Error("Directory already exists");
		}

		if (!this.parentPathsExist(cleanPath)) {
			throw new Error("Parent directories do not exist");
		}

		// Create the directory
		this.setYFileObject({ path: cleanPath, type: FileType.Directory });
	}

	/**
	 * Reads a directory from the filesystem.
	 *
	 * @param {string} path - The path of the directory.
	 * @returns {FileObject[]} An array of file objects in the directory.
	 * @throws {Error} If the directory is not found or the path is not a directory.
	 */
	public readdir(path: string) {
		// makes sure the path is a directory, and that it exists
		this.getDir(path);

		// Collect all keys that are in the `path` directory (not including `path` itself)
		const keysInDirectory: string[] = [];
		for (const key of this.fsDoc.keys()) {
			const qualifiedPath = path.endsWith("/") ? path : `${path}/`;
			if (key.startsWith(qualifiedPath) && key !== path) {
				keysInDirectory.push(key);
			}
		}

		return keysInDirectory.map((key) => this.yFileToFileObject(key, false));
	}

	/**
	 * Writes a file to the filesystem.
	 *
	 * @param {string} path - The path of the file.
	 * @param {Uint8Array | string} contents - The contents of the file.
	 * @throws {Error} If the file parent directories do not exist.
	 */
	public writeFile(path: string, contents: Uint8Array | string) {
		let cleanPath = path;
		if (!path.startsWith("/")) {
			cleanPath = `/${path}`;
		}

		const file = this.fsDoc.get(cleanPath);

		if (!this.parentPathsExist(cleanPath)) {
			throw new Error(`Parent directories do not exist: ${cleanPath}`);
		}

		this.setYFileObject({
			path: cleanPath,
			type: FileType.File,
			contents: new Y.Text(
				typeof contents === "string"
					? contents
					: new TextDecoder().decode(contents),
			),
		});
	}

	// Simply returns the file or directory type
	public getType(path: string): FileType {
		const file = this.fsDoc.get(path);
		if (!file) {
			throw new Error(`File or Directory not found: ${path}`);
		}

		return file.get("type")?.toString() as FileType;
	}

	// Does the node exist?
	public nodeExists(path: string): boolean {
		return this.fsDoc.has(path);
	}

	/**
	 * Given a file path, return the contents of the file as a FileObject.
	 *
	 * @param {string} path - The path of the file.
	 * @returns {FileObject} The file object with its contents.
	 * @throws {Error} If the file is not found or the path is not a file.
	 */
	public readFile(path: string): FileObject {
		const file = this.getFile(path);
		const contents = file.get("contents");

		return {
			path,
			type: FileType.File,
			contents,
		};
	}

	/**
	 * Renames a file or directory.
	 *
	 * @param {string} oldPath - The current path of the file or directory.
	 * @param {string} newPath - The new path of the file or directory.
	 * @throws {Error} If the parent directories of the new path do not exist or the file/directory is not found.
	 */
	public rename(oldPath: string, newPath: string) {
		// Make sure the new path exists
		if (!this.parentPathsExist(newPath)) {
			throw new Error("Parent directories do not exist");
		}

		const oldObject = this.fsDoc.get(oldPath);
		if (!oldObject) {
			throw new Error("File or directory not found");
		}

		this.fsDoc.set(newPath, oldObject.clone());
		this.fsDoc.delete(oldPath);
	}

	/**
	 * Removes a file or directory from the filesystem.
	 *
	 * @param {string} path - The path of the file or directory to remove.
	 */
	public rm(path: string) {
		this.fsDoc.delete(path);
	}

	/**
	 * Converts the Yjs file system to a format suitable for React tree components.
	 * The returned object has each path as a key, with properties for UI rendering.
	 *
	 * @param {string} docPath - The starting path for the tree (default: "/")
	 * @returns {Record<string, {index: string, canMove: boolean, isFolder: boolean, children: string[], data: string, canRename: boolean}>}
	 *          The file system as a tree structure for React components
	 */
	public toReactComplexTree(docPath = "/"): Record<
		string,
		{
			index: string;
			canMove: boolean;
			isFolder: boolean;
			children: string[];
			data: string;
			canRename: boolean;
		}
	> {
		const result: Record<
			string,
			{
				index: string;
				canMove: boolean;
				isFolder: boolean;
				children: string[];
				data: string;
				canRename: boolean;
			}
		> = {};

		const allPaths = Array.from(this.fsDoc.keys());

		const extractDisplayName = (currentKey: string): string => {
			if (currentKey === "/") {
				return "root";
			}
			let tempKey = currentKey.startsWith("/")
				? currentKey.substring(1)
				: currentKey;
			tempKey = tempKey.replace(/\/+$/, "");
			const parts = tempKey.split("/");
			return parts[parts.length - 1] || "";
		};

		allPaths.forEach((currentKey) => {
			try {
				const type = this.getType(currentKey);
				const isFolder = type === FileType.Directory;

				const displayName = extractDisplayName(currentKey);
				const parentDirKeys = this.getParentPaths(currentKey);

				const canRename = currentKey !== "/";
				const canMove = currentKey !== "/";

				let childrenPaths: string[] = [];
				if (isFolder) {
					// Get direct children of this directory
					childrenPaths = this.getDirectChildren(currentKey, allPaths);
				}

				result[currentKey === "/" ? "root" : currentKey] = {
					index: currentKey === "/" ? "root" : currentKey,
					data: displayName,
					canMove,
					canRename,
					isFolder,
					children: childrenPaths.sort(),
				};
			} catch (_error) {}
		});

		return result;
	}
}
